// feature-flags.js
//
// Configuration → Feature Flags screen (spec §3.4; plan Task 4.3). This is the
// first atomic screen extraction: it takes over the Systems + Feature-Modules
// capability that used to live on the Dashboard (`renderSystems`/`renderModules`
// in dashboard.js), regrouping the ~76 module flags into the six themed §5
// buckets plus a fail-closed "Other" bucket, and keeping the 8 visible emulator
// systems in their own always-expanded section anchored at #ff-systems (the
// /admin/systems route scrolls here).
//
// Correctness centrepiece — the mutation path is fail-closed. A toggle:
//   * serializes the WHOLE lane (every mutable feature+system switch disabled
//     across the reason prompt, any step-up, the PATCH, and the authoritative
//     reload; the 7 always-on LOCKED switches were already disabled and STAY so,
//     restored to their captured baseline, never blanket-enabled);
//   * keeps `runSensitiveAdminMutation` EXACT as the security boundary and creates
//     its ONE deadline/operation LAZILY inside the wrapper's `mutate(reason)`
//     callback, spanning only PATCH + authoritative GET;
//   * classifies the outcome three ways — DEFINITIVE non-apply (guard decline,
//     400, final 403) → revert; APPLIED + reload validated/matched → render the
//     reloaded state; INDETERMINATE (timeout/network/5xx/wrong-echo/reload-fail
//     or mismatch) → leave the optimistic value, never claim saved, surface
//     "couldn't confirm — reload the page."
//
// Lifecycle: the Task 3.1 Step-3c idiom with THREE generation lanes — `loadGen`
// (initial GET), `mutGen` (toggles), `romsStatusGen` (Refresh ROMs). One
// persistent ctx.onTeardown bumps all three, disposes the screen-level listeners,
// and cancels the /admin/systems scroll rAF. Row handlers use EVENT DELEGATION on
// the section root so an authoritative reload re-render never rebinds per-row.

import { api } from '../api.js';
import { runSensitiveAdminMutation, parseAdminError } from '../admin-security.js';
import { createOperation, createGeneration, whenAborted, navAbort } from '../lib/admin-lifecycle.js';
import { toError } from '../lib/admin-validate.js';
import { MODULE_META, SYSTEM_META, GROUPS } from './dashboard.js';
import { escapeHtml } from '../../../../src/shared/escape-html.js';

const EP = '/api/admin/features';

// The old dashboard auto-cleared the Refresh-ROMs status a few seconds after it
// showed. Restored here as a generation-guarded, teardown-cancelled timer (never
// an uncancelled one).
const ROMS_STATUS_CLEAR_MS = 5000;

// The catalog key sets. KNOWN_SYSTEM_KEYS includes the hidden system:gbc alias;
// VISIBLE_SYSTEMS is the 8 the grid renders (gbc filtered out).
export const KNOWN_FLAG_KEYS = Object.keys(MODULE_META);
export const KNOWN_SYSTEM_KEYS = Object.keys(SYSTEM_META);
export const VISIBLE_SYSTEMS = KNOWN_SYSTEM_KEYS.filter((k) => !SYSTEM_META[k].hidden);

// The final catch-all bucket for any live key whose catalog entry is missing or
// carries an absent/unrecognised `group`. Never throws, never drops a flag.
const OTHER_GROUP = { id: 'other', label: 'Other', icon: null, blurb: 'Uncategorised flags not mapped to a group.' };

// ── Pure helpers (exported for unit tests) ───────────────────────────────

// A live key is a real module flag iff it is not a system:/theme: prefixed key,
// not the operational discordInviteUrl string, and carries a boolean value.
// Unknown boolean keys are allowed through (they render under "Other").
export function isFlagKey(k, v) {
  if (k.startsWith('system:') || k.startsWith('theme:')) return false;
  if (k === 'discordInviteUrl') return false;
  return typeof v === 'boolean';
}

// Structured error used at EVERY failure site: { ok:false, error:{ kind, endpoint, status, ... } }.
const shapeError = (message, cause = null) =>
  ({ ok: false, error: { kind: 'shape', endpoint: EP, status: 200, message, cause } });

// Modules and Systems validate INDEPENDENTLY so a malformed Systems payload never
// suppresses the otherwise-valid Feature-Modules section (and vice-versa).
export function validateModules(features) {
  if (!features || typeof features !== 'object' || Array.isArray(features)) return shapeError('payload is not an object');
  for (const k of KNOWN_FLAG_KEYS) {
    if (!Object.hasOwn(features, k)) return shapeError(`missing flag ${k}`);
    if (typeof features[k] !== 'boolean') return shapeError(`non-boolean flag ${k}`);
  }
  return { ok: true };
}
export function validateSystems(features) {
  if (!features || typeof features !== 'object' || Array.isArray(features)) return shapeError('payload is not an object');
  for (const k of KNOWN_SYSTEM_KEYS) {
    if (Object.hasOwn(features, k) && typeof features[k] !== 'boolean') return shapeError(`non-boolean system ${k}`);
  }
  if (Object.hasOwn(features, 'discordInviteUrl') && typeof features.discordInviteUrl !== 'string') {
    return shapeError('discordInviteUrl is not a string');
  }
  return { ok: true };
}

// A key's group id, fail-closed: missing entry OR absent/unknown group → 'other'.
export function resolveGroup(key) {
  const gid = MODULE_META[key]?.group;
  return GROUPS.some((g) => g.id === gid) ? gid : OTHER_GROUP.id;
}

// "Modified" only when the catalog carries an authoritative boolean default for a
// KNOWN key — an unknown live key never dereferences a missing default.
export function isModified(key, value) {
  const meta = MODULE_META[key];
  return !!meta && typeof meta.default === 'boolean' && value !== meta.default;
}

// Systems display rule: default-ON when the key is absent (carried from dashboard).
export const systemEnabled = (key, features) => features[key] !== false;

// Snake/colon key → Title Case fallback label (identical to the old dashboard fallback).
export function humanize(key) {
  return String(key).replace(/[:_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// Effective value for reload/echo comparison: systems use default-ON semantics,
// modules use the raw boolean.
function effectiveValue(key, features) {
  return key.startsWith('system:') ? features[key] !== false : features[key];
}

// PATCH success is validated BY VALUE: every requested key present AND its echoed
// value strictly equals the requested one (incl. the paired system:gbc).
function echoMatches(resp, updates) {
  if (!resp || typeof resp !== 'object' || Array.isArray(resp)) return false;
  for (const [k, v] of Object.entries(updates)) {
    if (!Object.hasOwn(resp, k) || resp[k] !== v) return false;
  }
  return true;
}

// A structured error is a DEFINITIVE non-apply only for a pre-apply HTTP rejection
// (400 reason_required/bad_body; a final authorization 403 after the wrapper's own
// step-up retry). Everything else — 5xx, network, timeout, wrong echo, reload
// failure/mismatch — is INDETERMINATE (fail closed: never revert, never "saved").
// NOTE: deliberately NARROWER than database.js's is4xxNonApply (any 4xx) — this
// screen only treats the step-up 403 / reason-required 400 pre-apply codes as
// definitive; do not "unify" the two.
function isPreApplyRejection(err) {
  return !!err && err.kind === 'http' && (err.status === 400 || err.status === 403);
}

// A genuine step-up sentinel is ONLY the expected PRE-APPLY HTTP 403 carrying the
// admin_step_up_required code. A 5xx (or any non-403) that merely carries that
// payload is a contract violation and MUST stay INDETERMINATE per the fail-closed
// spec — so it is NOT a definitive non-apply: do not reset patchSent, and do not let
// the terminal classifier treat it as definitive (which would revert an outcome the
// server may actually have applied). Takes the RAW error (to parse its body) and
// confirms the mapped HTTP status via toError.
function isStepUpSentinel(rawErr) {
  if (parseAdminError(rawErr)?.error !== 'admin_step_up_required') return false;
  const e = toError(EP, rawErr).error;
  return e.kind === 'http' && e.status === 403;
}

// The moved feature cache — a toggle reloads state through it, and re-renders read it.
let _features = null;

// ── Markup builders ──────────────────────────────────────────────────────

const CHEVRON = '<svg class="bucket-chevron" viewBox="0 0 16 16" aria-hidden="true"><path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.75"/></svg>';

function bucketIconSvg(group) {
  if (group.icon) return `<svg class="bucket-icon" viewBox="0 0 24 24" aria-hidden="true"><use href="#${group.icon}"></use></svg>`;
  // "Other" has no sprite symbol — a neutral inline square keeps the header uniform.
  return '<svg class="bucket-icon" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.75"><rect x="4" y="4" width="16" height="16"/></svg>';
}

// One flag row. Curated MODULE_META label/desc are trusted static strings; the
// API-derived fallback label and the raw key (flag-key + data-module) are escaped.
function flagRowHtml(key, value) {
  const meta = MODULE_META[key];
  const known = !!meta;
  // `rawLabel` feeds the switch's aria-label (spec §3.8 — the `.sw-track` span
  // is decorative/empty, so without this the checkbox's accessible name is
  // blank). `label` is the EXISTING display value: a known key's meta.label is
  // trusted and used unescaped in text content below; an unknown key's is
  // already escapeHtml()'d. Escaping rawLabel fresh for the attribute avoids
  // double-escaping the unknown-key branch.
  const rawLabel = known ? meta.label : humanize(key);
  const label = known ? meta.label : escapeHtml(rawLabel);
  const desc = known ? meta.desc : '';
  const locked = known && meta.always === true;
  const modified = isModified(key, value);
  const safeKey = escapeHtml(key);
  return `<div class="flag${locked ? ' is-locked' : ''}" data-module="${safeKey}">
      <div class="flag-main">
        <div class="flag-name">${label} <code class="flag-key">${safeKey}</code></div>
        ${desc ? `<p class="flag-desc">${desc}</p>` : ''}
      </div>
      <span class="flag-dot${modified ? ' is-modified' : ''}"${modified ? ' title="Modified from default"' : ''}></span>
      ${locked ? '<span class="pill accent">Always on</span>' : ''}
      <label class="sw"><input type="checkbox" data-module="${safeKey}" aria-label="${escapeHtml(rawLabel)}"${value ? ' checked' : ''}${locked ? ' disabled' : ''}><span class="sw-track"></span></label>
    </div>`;
}

function systemRowHtml(key, features) {
  const meta = SYSTEM_META[key];
  const enabled = systemEnabled(key, features);
  const safeKey = escapeHtml(key);
  return `<div class="flag" data-system-flag="${safeKey}">
      <div class="flag-main">
        <div class="flag-name">${meta.label} <code class="flag-key">${safeKey}</code></div>
        <p class="flag-desc">${meta.desc}</p>
      </div>
      <label class="sw"><input type="checkbox" data-system-flag="${safeKey}" aria-label="${escapeHtml(meta.label)}"${enabled ? ' checked' : ''}><span class="sw-track"></span></label>
    </div>`;
}

// ── Screen ───────────────────────────────────────────────────────────────

export function renderFeatureFlags(params, ctx) {
  const root = document.querySelector('[data-screen="feature-flags"]');
  if (!root) return undefined;

  const el = (id) => root.querySelector(`#${id}`);
  const searchEl = el('ff-search');
  const modifiedOnlyEl = el('ff-modified-only');
  const systemsAnchor = el('ff-systems');
  const bucketsEl = el('ff-buckets');
  const modulesErrEl = el('ff-modules-error');
  const statusEl = el('ff-status');
  const countTotal = el('ff-count-total');
  const countOn = el('ff-count-on');
  const countModified = el('ff-count-modified');

  // Three independent state lanes (Step 3c). Every operation bumps its lane on entry.
  const loadGen = createGeneration();
  const mutGen = createGeneration();
  const romsStatusGen = createGeneration();

  const offs = [];
  const on = (target, ev, fn) => { target.addEventListener(ev, fn); offs.push(() => target.removeEventListener(ev, fn)); };
  let rafId = 0;
  let romsClearTimer = 0;   // pending Refresh-ROMs status auto-clear (cancelled by teardown)

  // ONE persistent teardown: invalidate every lane, dispose listeners, cancel the frame.
  ctx.onTeardown(() => {
    const errors = [];
    try { loadGen.bump(); mutGen.bump(); romsStatusGen.bump(); } catch (e) { errors.push(e); }
    for (const off of offs) { try { off(); } catch (e) { errors.push(e); } }
    if (rafId) { try { cancelAnimationFrame(rafId); } catch (e) { errors.push(e); } }
    if (romsClearTimer) { try { clearTimeout(romsClearTimer); romsClearTimer = 0; } catch (e) { errors.push(e); } }
    if (errors.length) return { ok: false, errors };
    return undefined;
  });

  const setStatus = (text, cls = '') => { if (statusEl) { statusEl.textContent = text; statusEl.className = `admin-status${cls ? ' ' + cls : ''}`; } };

  // ── Rendering ──────────────────────────────────────────────────────────

  function renderSummary(features) {
    let total = 0, onCount = 0, modified = 0;
    for (const [k, v] of Object.entries(features)) {
      if (!isFlagKey(k, v)) continue;
      total += 1;
      if (v) onCount += 1;
      if (isModified(k, v)) modified += 1;
    }
    if (countTotal) countTotal.textContent = String(total);
    if (countOn) countOn.textContent = String(onCount);
    if (countModified) countModified.textContent = String(modified);
  }
  function renderSummaryError() {
    for (const c of [countTotal, countOn, countModified]) if (c) c.textContent = '–';
  }

  function renderBuckets(features) {
    // Bin live flag keys into groups in encounter order; render groups in §5 order.
    const bins = new Map();
    for (const g of [...GROUPS, OTHER_GROUP]) bins.set(g.id, []);
    for (const [k, v] of Object.entries(features)) {
      if (!isFlagKey(k, v)) continue;
      bins.get(resolveGroup(k)).push([k, v]);
    }
    const parts = [];
    for (const g of [...GROUPS, OTHER_GROUP]) {
      const rows = bins.get(g.id);
      if (!rows.length) continue;
      const onCount = rows.filter(([, v]) => v).length;
      const bodyId = `ff-bucket-${g.id}`;
      parts.push(`<div class="bucket" data-group="${g.id}">
        <button type="button" class="bucket-head" aria-expanded="false" aria-controls="${bodyId}">
          ${bucketIconSvg(g)}
          <div class="bucket-title"><span class="bucket-name">${g.label}</span><span class="bucket-blurb">${g.blurb}</span></div>
          <span class="pill">${onCount} / ${rows.length}</span>
          ${CHEVRON}
        </button>
        <div class="bucket-body" id="${bodyId}" hidden>${rows.map(([k, v]) => flagRowHtml(k, v)).join('')}</div>
      </div>`);
    }
    bucketsEl.innerHTML = parts.join('');
  }

  // No argument: the modules band shows a single fixed fail-closed message on any
  // shape/load failure (the two callers previously passed inconsistent shapes into
  // an ignored param — a structured error vs a {ok,error} wrapper).
  function renderModulesError() {
    bucketsEl.innerHTML = '';
    if (modulesErrEl) {
      modulesErrEl.hidden = false;
      modulesErrEl.className = 'admin-status error';
      modulesErrEl.textContent = 'Feature modules unavailable: the /api/admin/features response was malformed. Reload the page.';
    }
    renderSummaryError();
  }
  function clearModulesError() { if (modulesErrEl) { modulesErrEl.hidden = true; modulesErrEl.textContent = ''; } }

  function systemsShellHtml(inner) {
    const bodyId = 'ff-systems-body';
    return `<div class="bucket" data-systems>
      <button type="button" class="bucket-head" aria-expanded="true" aria-controls="${bodyId}">
        ${bucketIconSvg({ icon: null })}
        <div class="bucket-title"><span class="bucket-name">Emulator Systems</span><span class="bucket-blurb">Enable or disable emulator systems. Disabled ones vanish from the UI and room browser.</span></div>
        <span class="pill" id="ff-systems-count"></span>
        ${CHEVRON}
      </button>
      <div class="bucket-body" id="${bodyId}">${inner}</div>
    </div>`;
  }
  const refreshToolsHtml = `<div class="ff-systems-tools" style="margin-top:12px;display:flex;align-items:center;gap:8px;">
      <button type="button" id="ff-refresh-roms" class="btn">Refresh ROMs</button>
      <span id="ff-refresh-roms-status" class="admin-status" role="status" aria-live="polite"></span>
    </div>`;

  function renderSystems(features) {
    const rows = VISIBLE_SYSTEMS.map((k) => systemRowHtml(k, features)).join('');
    systemsAnchor.innerHTML = systemsShellHtml(`<div id="ff-systems-error" class="admin-status" role="alert" aria-live="assertive" hidden></div><div class="ff-systems-grid">${rows}</div>${refreshToolsHtml}`);
    const onCount = VISIBLE_SYSTEMS.filter((k) => systemEnabled(k, features)).length;
    const pill = el('ff-systems-count');
    if (pill) pill.textContent = `${onCount} / ${VISIBLE_SYSTEMS.length}`;
  }
  function renderSystemsError() {
    systemsAnchor.innerHTML = systemsShellHtml(`<div id="ff-systems-error" class="admin-status error" role="alert" aria-live="assertive">Emulator systems unavailable: the /api/admin/features response was malformed. Reload the page.</div>${refreshToolsHtml}`);
  }

  function renderAll(features) {
    const mods = validateModules(features);
    const sys = validateSystems(features);
    // Independent: each section renders or errors on its own validation.
    if (sys.ok) renderSystems(features); else renderSystemsError();
    if (mods.ok) { clearModulesError(); renderBuckets(features); renderSummary(features); }
    else renderModulesError();
    applyFilters();
  }

  // ── Collapse + filters ─────────────────────────────────────────────────

  function setExpanded(bucket, expanded) {
    const head = bucket.querySelector('.bucket-head');
    const body = bucket.querySelector('.bucket-body');
    if (head) head.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    if (body) body.hidden = !expanded;
  }

  function applyFilters() {
    const q = (searchEl?.value || '').trim().toLowerCase();
    const modOnly = !!modifiedOnlyEl?.checked;
    const filtering = q !== '' || modOnly;

    // Module buckets.
    for (const bucket of bucketsEl.querySelectorAll('.bucket')) {
      let anyVisible = false;
      for (const rowEl of bucket.querySelectorAll('.flag')) {
        const key = rowEl.getAttribute('data-module') || '';
        const meta = MODULE_META[key];
        const label = meta ? meta.label : humanize(key);
        const desc = meta ? meta.desc : '';
        const hay = `${label} ${key} ${desc}`.toLowerCase();
        const matchesSearch = !q || hay.includes(q);
        const isMod = rowEl.querySelector('.flag-dot')?.classList.contains('is-modified');
        const visible = matchesSearch && (!modOnly || !!isMod);
        rowEl.hidden = !visible;
        anyVisible = anyVisible || visible;
      }
      bucket.hidden = filtering && !anyVisible;
      setExpanded(bucket, filtering ? anyVisible : false);
    }

    // Systems bucket: search filters its rows; "Modified only" hides the whole
    // section (systems have no default/modified concept).
    const sysBucket = systemsAnchor.querySelector('.bucket');
    if (sysBucket) {
      let anyVisible = false;
      for (const rowEl of sysBucket.querySelectorAll('.flag')) {
        const key = rowEl.getAttribute('data-system-flag') || '';
        const meta = SYSTEM_META[key];
        const hay = `${meta ? meta.label : ''} ${key} ${meta ? meta.desc : ''}`.toLowerCase();
        const visible = (!q || hay.includes(q)) && !modOnly;
        rowEl.hidden = !visible;
        anyVisible = anyVisible || visible;
      }
      if (modOnly) {
        sysBucket.hidden = true;
        setExpanded(sysBucket, false);
      } else {
        sysBucket.hidden = q !== '' && !anyVisible;
        setExpanded(sysBucket, q === '' ? true : anyVisible);
      }
    }
  }

  // ── Mutation lane ──────────────────────────────────────────────────────

  const allSwitches = () => Array.from(root.querySelectorAll('input[data-module], input[data-system-flag]'));

  // Group ids of the module buckets the operator currently has expanded, so an
  // authoritative re-render (after a saved toggle) can restore their place. The
  // applyFilters "collapse when not filtering" reset is intentional for search,
  // but an in-place save must not lose the operator's open section.
  function expandedModuleGroups() {
    const open = new Set();
    for (const b of bucketsEl.querySelectorAll('.bucket')) {
      if (b.querySelector('.bucket-head')?.getAttribute('aria-expanded') === 'true') open.add(b.getAttribute('data-group'));
    }
    return open;
  }

  async function doMutation({ updates, actionLabel, revert }) {
    const switches = allSwitches();
    const baselines = switches.map((s) => s.disabled);
    const token = mutGen.bump();
    // Supersede any still-in-flight initial load. The initial GET lives in a
    // SEPARATE lane (loadGen) and also calls renderAll, and the router retains
    // this screen's hidden DOM across navigation — so on a re-entry a pending GET
    // could resolve AFTER this mutation's authoritative reload and clobber the
    // confirmed UI (or re-render enabled switches mid-prompt and expose a second
    // mutation). Bumping loadGen makes that pending GET stale so it self-suppresses
    // at its own `loadGen.isStale(token)` guard, serialising load and mutation.
    loadGen.bump();
    switches.forEach((s) => { s.disabled = true; });
    setStatus('Saving…', 'saving');

    // Captured from inside `mutate` (the wrapper returns only a boolean).
    let mutationResult = null;
    let wrapperThrew = null;
    // Set true the instant a PATCH is put on the wire. A wrapper throw while this
    // is still false means the failure happened during PREFLIGHT (reason/step-up),
    // before anything was sent → the change definitively did NOT apply → revert.
    let patchSent = false;

    try {
      let completed = false;
      try {
        completed = await runSensitiveAdminMutation({
          actionLabel,
          // ONE deadline/operation, created LAZILY per attempt, spanning PATCH + authoritative GET.
          mutate: async (reason) => {
            const op = createOperation();
            const unregister = ctx.onTeardown(() => op.abort(navAbort()));
            let abortWait = null;
            try {
              abortWait = whenAborted(op.signal);
              let patchResp;
              patchSent = true;   // a PATCH is now on the wire → any later uncertain failure is INDETERMINATE, never a revert
              try {
                patchResp = await Promise.race([
                  api.patch(EP, { updates, reason }, { signal: op.signal }),
                  abortWait.promise,
                ]);
              } catch (err) {
                // Do NOT swallow the retry sentinel — rethrow so the wrapper owns step-up + a 2nd attempt.
                // RESET patchSent first: a genuine step-up 403 means the server REFUSED this PATCH pending
                // step-up (provably not applied), so this attempt must not poison the terminal
                // classification. If the wrapper's step-up preflight then fails, patchSent stays false →
                // the failure is correctly classed a DEFINITIVE non-apply (revert). A successful step-up
                // retry sets patchSent=true again when it actually sends the second PATCH. A non-403
                // carrying the step-up payload is NOT a sentinel — it flows through as an indeterminate error.
                if (isStepUpSentinel(err)) { patchSent = false; throw err; }
                mutationResult = { ok: false, error: toError(EP, err).error };
                return;
              }
              if (mutGen.isStale(token) || op.closed) return;
              if (!echoMatches(patchResp, updates)) {
                mutationResult = { ok: false, applied: patchResp, error: shapeError('PATCH echo did not match requested values').error };
                return;
              }
              let reloadResp;
              try {
                reloadResp = await Promise.race([
                  api.get(EP, { signal: op.signal }),
                  abortWait.promise,
                ]);
              } catch (err) {
                mutationResult = { ok: false, applied: patchResp, error: toError(EP, err).error };
                return;
              }
              if (mutGen.isStale(token) || op.closed) return;
              const vM = validateModules(reloadResp);
              const vS = validateSystems(reloadResp);
              if (!vM.ok) { mutationResult = { ok: false, applied: patchResp, error: vM.error }; return; }
              if (!vS.ok) { mutationResult = { ok: false, applied: patchResp, error: vS.error }; return; }
              for (const [k, v] of Object.entries(updates)) {
                if (effectiveValue(k, reloadResp) !== v) {
                  mutationResult = { ok: false, applied: patchResp, error: shapeError('reload value did not match requested value').error };
                  return;
                }
              }
              mutationResult = { ok: true, applied: patchResp, reload: reloadResp };
            } finally {
              if (abortWait) abortWait.cancel();
              op.finish();
              unregister();
            }
          },
        });
      } catch (err) {
        wrapperThrew = err;
      }

      // Terminal presentation + revert use the LANE TOKEN only: a current screen
      // recovers after a timeout, a departed screen stays untouched.
      if (mutGen.isStale(token)) return;

      if (wrapperThrew) {
        // Three DEFINITIVE-non-apply shapes reach here: a 400/403 pre-apply HTTP
        // rejection; a step-up sentinel surviving the wrapper's own retry (step-up
        // did not take → provably not applied); OR a non-sentinel throw with NO
        // PATCH yet on the wire (patchSent === false — e.g. the preflight step-up
        // status GET failed network/5xx), which sent nothing and so did not apply.
        // Only a throw AFTER a PATCH was sent (patchSent === true, e.g. 5xx/network/
        // timeout on the PATCH itself) is INDETERMINATE — the PATCH may have landed.
        const e = toError(EP, wrapperThrew).error;
        // A sentinel is a genuine step-up 403 only (isStepUpSentinel) — a 5xx/other
        // status carrying the step-up payload is NOT definitive and must stay indeterminate.
        const sentinel = isStepUpSentinel(wrapperThrew);
        const definitive = isPreApplyRejection(e) || sentinel || (!patchSent && !mutationResult?.applied);
        if (definitive) {
          revert();
          setStatus(`Error: ${wrapperThrew.message || 'update rejected'}`, 'error');
        } else {
          setStatus("Couldn't confirm. Reload the page.", 'error');
        }
        return;
      }

      if (!completed) {
        // Reason or step-up declined BEFORE a successful attempt → definitive non-apply.
        revert();
        setStatus('', '');
        return;
      }

      if (mutationResult && mutationResult.ok) {
        _features = mutationResult.reload;
        const keepOpen = expandedModuleGroups();      // preserve the operator's expanded buckets across the re-render
        renderAll(_features);
        for (const b of bucketsEl.querySelectorAll('.bucket')) {
          if (!b.hidden && keepOpen.has(b.getAttribute('data-group'))) setExpanded(b, true);
        }
        setStatus('Saved.', 'success');
        return;
      }

      // A recorded failure from inside mutate. Once `applied` is present the PATCH
      // PROVABLY landed (2xx + value-matched echo), so ANY later failure — including
      // a reload GET returning 400/403 — is INDETERMINATE, never a revert: reverting
      // would show the OLD value while the server holds the NEW one. Only a failure
      // with NO `applied` (the PATCH itself was rejected pre-apply) can revert.
      const e = mutationResult?.error;
      if (!mutationResult?.applied && isPreApplyRejection(e)) {
        revert();
        setStatus(`Error: ${e.cause?.message || 'update rejected'}`, 'error');
      } else {
        // INDETERMINATE — leave the optimistic value, never claim saved, never revert.
        setStatus("Couldn't confirm. Reload the page.", 'error');
      }
    } finally {
      // Restore EACH switch to its captured BASELINE (locked stays locked) — lane token only.
      if (!mutGen.isStale(token)) {
        switches.forEach((s, i) => { s.disabled = baselines[i]; });
      }
    }
  }

  // ── Refresh ROMs (own lane) ────────────────────────────────────────────

  async function refreshRoms() {
    const btn = el('ff-refresh-roms');
    const status = el('ff-refresh-roms-status');
    if (!btn) return;
    const token = romsStatusGen.bump();
    btn.disabled = true;
    if (status) { status.textContent = 'Refreshing…'; status.className = 'admin-status saving'; }
    const op = createOperation();
    const unregister = ctx.onTeardown(() => op.abort(navAbort()));
    let abortWait = null;
    try {
      abortWait = whenAborted(op.signal);
      const resp = await Promise.race([api.post('/api/admin/refresh-roms', undefined, { signal: op.signal }), abortWait.promise]);
      if (romsStatusGen.isStale(token) || op.closed) return;
      if (!resp || !Array.isArray(resp.systems)) {
        if (status) { status.textContent = 'Error: unexpected response.'; status.className = 'admin-status error'; }
        return;
      }
      if (status) { status.textContent = `Done: ${resp.systems.length} systems cached.`; status.className = 'admin-status success'; }
    } catch (err) {
      if (romsStatusGen.isStale(token)) return;
      if (status) { status.textContent = `Error: ${err.message || 'refresh failed'}`; status.className = 'admin-status error'; }
    } finally {
      if (abortWait) abortWait.cancel();
      op.finish();
      unregister();
      if (!romsStatusGen.isStale(token)) {
        btn.disabled = false;
        // Auto-clear the terminal status after a few seconds. Generation-guarded
        // (a newer refresh's token supersedes this one) and cancelled by the
        // persistent teardown — never an uncancelled timer.
        if (status) {
          if (romsClearTimer) clearTimeout(romsClearTimer);
          romsClearTimer = setTimeout(() => {
            romsClearTimer = 0;
            if (romsStatusGen.isStale(token)) return;
            status.textContent = '';
            status.className = 'admin-status';
          }, ROMS_STATUS_CLEAR_MS);
        }
      }
    }
  }

  // ── Delegated listeners (bound once, survive re-render) ─────────────────

  function onDelegatedClick(e) {
    if (e.target.closest('#ff-refresh-roms')) { e.preventDefault(); refreshRoms(); return; }
    const head = e.target.closest('.bucket-head');
    if (head && root.contains(head)) {
      const bucket = head.closest('.bucket');
      const expanded = head.getAttribute('aria-expanded') === 'true';
      setExpanded(bucket, !expanded);
    }
  }

  function onDelegatedChange(e) {
    const input = e.target;
    if (!(input instanceof HTMLInputElement)) return;
    const isModule = input.matches('input[data-module]');
    const isSystem = input.matches('input[data-system-flag]');
    if (!isModule && !isSystem) return;
    if (input.disabled) return; // locked switches never mutate

    const value = input.checked;
    const key = isModule ? input.dataset.module : input.dataset.systemFlag;
    const updates = { [key]: value };
    // gb ≡ gbc: a Game Boy system toggle carries the hidden gbc alias with the same value.
    if (key === 'system:gb') updates['system:gbc'] = value;

    doMutation({
      updates,
      actionLabel: `change the ${key} feature flag`,
      revert: () => { input.checked = !value; },
    });
  }

  // Search + "Modified only" live on the static scaffold → bind directly (once).
  if (searchEl) on(searchEl, 'input', applyFilters);
  if (modifiedOnlyEl) on(modifiedOnlyEl, 'change', applyFilters);
  on(root, 'click', onDelegatedClick);
  on(root, 'change', onDelegatedChange);

  // ── Initial load (loadGen) ─────────────────────────────────────────────

  const loadPromise = (async () => {
    const token = loadGen.bump();
    // The router retains this screen's DOM across navigation, so on a RE-ENTRY the
    // previous instance's switches are still present and clickable while this fresh
    // GET is pending. Disable them up front so a stale, about-to-be-replaced control
    // can't start a mutation against a superseded value while the load is in flight
    // (renderAll re-enables them on success; the error paths replace them entirely).
    allSwitches().forEach((s) => { s.disabled = true; });
    const op = createOperation();
    const unregister = ctx.onTeardown(() => op.abort(navAbort()));
    let abortWait = null;
    try {
      abortWait = whenAborted(op.signal);
      const resp = await Promise.race([api.get(EP, { signal: op.signal }), abortWait.promise]);
      if (loadGen.isStale(token) || op.closed) return;
      _features = resp;
      renderAll(_features);
    } catch (err) {
      if (loadGen.isStale(token)) return;
      // Total load failure → both sections show a structured error.
      renderSystemsError();
      renderModulesError();
      setStatus(`Error: ${err.message || 'failed to load feature flags'}`, 'error');
    } finally {
      if (abortWait) abortWait.cancel();
      op.finish();
      unregister();
    }
  })();

  // /admin/systems scroll: a teardown-cancelable rAF (anchor exists statically).
  const scrollTo = ctx.scrollTo || params?.scrollTo;
  if (scrollTo) {
    rafId = requestAnimationFrame(() => { rafId = 0; root.querySelector(scrollTo)?.scrollIntoView({ block: 'start' }); });
  }

  return loadPromise;
}

// Exposed only for tests that assert the moved cache resets across screen loads.
export function __resetFeaturesCache() { _features = null; }
